import { defineStore } from 'pinia'
import tourSteps from '../../guided_tour/GuidedTourSteps'
import Shepherd from 'shepherd.js'
import 'shepherd.js/dist/css/shepherd.css'
import { Dialog } from 'quasar'

export const useTourStore = defineStore('tour', {
  state: () => ({
    phase: localStorage.getItem('tourPhase') || 'dashboard',
    activeTour: null,
    completed: false,
    userId: null,
  }),

  actions: {
    /**
     * Initializes the tour store.
     * @param {String} userId The user's unique identifier.
     * @param {boolean} showTutorial Whether to show the tutorial or not. Depends
     *  on the user - only demo users are shown the tour.
     */
    init(userId, showTutorial = false) {
      this.userId = userId
      if (!showTutorial) {
        this.completed = true
        return
      }

      this.completed = localStorage.getItem(`tourSeen:${userId}`) === 'true'
      this.phase = localStorage.getItem('tourPhase') || 'dashboard'
    },

    /**
     * Sets the current phase of the tour.
     * @param {String} phase The phase to be set.
     */
    setPhase(phase) {
      this.phase = phase
      localStorage.setItem('tourPhase', phase)
    },

    /**
     * Concludes the tour and marks it as completed. After that,
     * the tour will not be shown again unless it is restarted.
     */
    complete() {
      this.completed = true
      this.phase = 'done'
      localStorage.setItem(`tourSeen:${this.userId}`, 'true')
      localStorage.setItem('tourPhase', 'done')
    },

    /**
     * Restarts the tour. This method is triggered from the user's dashboard.
     */
    restart() {
      localStorage.removeItem(`tourSeen:${this.userId}`)
      localStorage.setItem('tourPhase', 'dashboard')
      this.completed = false
      this.setPhase('dashboard')
      this.initializeTour('dashboard')
    },

    /**
     * Given a phase, decides whether this phase of the tour should be
     * viewed or not.
     * @param {String} expectedPhase The phase to be checked.
     * @returns {boolean} True if the tour should be shown, false otherwise.
     */
    shouldShowTour(expectedPhase) {
      if (this.completed) return false

      return this.phase === expectedPhase
    },

    /**
     * Initializes the active tour object, i.e. the concrete tutorial that
     * the user will view.
     * @param {String} phase The phase of the tour. Check `tourSteps`
     *  for available phases.
     */
    async initializeTour(phase) {
      this.activeTour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          classes: 'shepherd-theme-arrows',
          scrollTo: false,
          arrow: false,
          modalOverlayOpeningPadding: 8,
          canClickTarget: false
        }
      })

      const steps = tourSteps(this.activeTour)[phase]
      steps.forEach(step => this.activeTour.addStep(step))

      await this.activeTour.start()
    },

    /**
     * Skips both the active tour and the tour as a whole.
     */
    skipActiveTour() {
      Dialog.create({
        title: 'Skip tour',
        message: 'Are you sure you want to skip the tour?',
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.complete()
        this.completeActiveTour()
      })
    },

    /**
     * Ends the active tour.
     */
    completeActiveTour() {
      if (this.activeTour === null) return

      this.activeTour.complete()
      /* Set the phase to the next one in case the tour interrupted suddenly. */
      this.setPhase(this._getNextPhase())

      this.activeTour = null
    },

    /**
     * Identifies the next state based on the current one.
     * @returns {string|null} The next state identifier.
     * @private
     */
    _getNextPhase() {
      const phases = ['dashboard', 'buildingsList', 'buildingDetails', 'scenarioShow', 'buildingLca']
      const currentIndex = phases.indexOf(this.phase)
      return phases[currentIndex + 1] || null
    }
  }
})